import React, { useState, useEffect, useRef, useCallback } from 'react';
import { auth } from '../../firebase';
import DrawingCanvas from './DrawingCanvas';
import ToolPanel from './ToolPanel';
import PageNavigation from './PageNavigation';
import EditTitleInNotebook from './EditTitleInNotebook';
import NotebookHeader from './NotebookHeader';
import SavedStrokesPanel from './SavedStrokesPanel';
import { toCanvasStroke } from './utils/strokeRenderer';
import './NotebookEditor.css';

function NotebookEditor({ notebook, onBack }) {
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [pageStrokesLoading, setPageStrokesLoading] = useState(false);
    const [isSubscriber, setIsSubscriber] = useState(false);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [pages, setPages] = useState([]);
    const [currentTool, setCurrentTool] = useState('pen');
    const [toolSettings, setToolSettings] = useState({
        color: '#000000',
        thickness: 2,
        opacity: 1.0
    });
    const [palmRejectionSettings, setPalmRejectionSettings] = useState({
        enabled: true,
        sensitivity: 'medium',
        inputMode: 'auto',
        edgeRejection: true,
        multiTouchFiltering: true
    });
    const [canvasBackground, setCanvasBackground] = useState('white');
    
    const getInitialCanvasSize = () => {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        return { width: viewportWidth, height: viewportHeight };
    };
    
    const [canvasSize, setCanvasSize] = useState(getInitialCanvasSize);
    const [sidebarVisible, setSidebarVisible] = useState(true);
    const [toolPanelVisible, setToolPanelVisible] = useState(true);
    const [savedStrokesPanelVisible, setSavedStrokesPanelVisible] = useState(false);
    const [contextMenuVisible, setContextMenuVisible] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    const [selectedStrokeForSaving, setSelectedStrokeForSaving] = useState(null);
    const [headerMinimized, setHeaderMinimized] = useState(false);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editingTitleValue, setEditingTitleValue] = useState(notebook?.title || '');
    
    // Dimension controls state
    const [showDimensionModal, setShowDimensionModal] = useState(false);
    const [dimensionInputs, setDimensionInputs] = useState({ width: 1200, height: 800 });
    
    // Ruler state
    const [rulerVisible, setRulerVisible] = useState(false);
    
    // Undo/Redo state
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);
    const [maxUndoSteps] = useState(50);
    
    // Use ref to always get current page index in async callbacks
    const currentPageIndexRef = useRef(currentPageIndex);
    
    // Track which notebook we've already initialized to prevent duplicate API calls
    const initializedNotebookRef = useRef(null);
    const loadedToolSettingsRef = useRef(false);
    
    useEffect(() => {
        currentPageIndexRef.current = currentPageIndex;
    }, [currentPageIndex]);

    // Track which pages have been loaded to avoid unnecessary API calls
    const loadedPagesRef = useRef(new Set());

    // Initialize pages
    useEffect(() => {
        if (notebook?.id && initializedNotebookRef.current !== notebook.id) {
            initializedNotebookRef.current = notebook.id;
            loadedToolSettingsRef.current = false;
            checkIfSubscriber();
            loadNotebookPages();
        }
    }, [notebook?.id]);

    // Update title when notebook changes
    useEffect(() => {
        if (notebook) {
            setEditingTitleValue(notebook.title);
        }
    }, [notebook?.title]);

    // Update canvas size based on current page dimensions (only for infinite mode)
    useEffect(() => {
        if (notebook?.page_mode === 'scaling') {
            return;
        }
        
        if (pages.length > 0 && currentPageIndex >= 0 && currentPageIndex < pages.length) {
            const currentPage = pages[currentPageIndex];
            if (currentPage && (currentPage.width || currentPage.height)) {
                const newCanvasSize = {
                    width: currentPage.width || 1200,
                    height: currentPage.height || 800
                };
                
                if (newCanvasSize.width !== canvasSize.width || newCanvasSize.height !== canvasSize.height) {
                    setCanvasSize(newCanvasSize);
                    setDimensionInputs(newCanvasSize);
                }
            }
        }
    }, [pages, currentPageIndex, notebook?.page_mode]);

    // Initialize background from notebook data
    useEffect(() => {
        if (notebook && notebook.background_type) {
            setCanvasBackground(notebook.background_type);
        }
    }, [notebook?.background_type]);

    // Load tool settings and user preferences once when notebook is loaded
    useEffect(() => {
        if (notebook?.id && !loadedToolSettingsRef.current) {
            loadedToolSettingsRef.current = true;
            loadToolSettingsFromBackend();
            loadUserPreferences();
        }
    }, [notebook?.id]);

    const loadToolSettingsFromBackend = async () => {
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token || !notebook?.id) return;

            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${notebook.id}/tool-settings/`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                        "requesting-user-uid": auth.currentUser.uid,
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                if (data.tool_settings) {
                    setToolSettings(prev => ({ ...prev, ...data.tool_settings }));
                }
            }
        } catch (error) {
            console.error('Error loading tool settings:', error);
        }
    };

    const checkIfSubscriber = async () => {
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;
            if (!token) return;
            
            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/users/${user.uid}/get-subscriber-status/`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                setIsSubscriber(data);
            }
        } catch (error) {
            console.error('Error checking subscriber status:', error);
        }
    };

    const loadUserPreferences = async () => {
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;
            if (!token) return;

            const response = await fetch(
                'https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/user/preferences/',
                {
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                        "requesting-user-uid": auth.currentUser.uid,
                    },
                }
            );

            if (response.ok) {
                const preferencesResponse = await response.json();
                const preferences = preferencesResponse.preferences || preferencesResponse;
                
                if (preferences.palmRejection) {
                    setPalmRejectionSettings(preferences.palmRejection);
                }
            }
        } catch (error) {
            console.error('Error loading user preferences:', error);
        }
    };

    // Define loadPageContent before the effect that uses it
    const loadPageContent = useCallback(async (pageId, pageIndex) => {
        setPageStrokesLoading(true);
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) return;

            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${notebook.id}/pages/${pageId}/`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                        "requesting-user-uid": auth.currentUser.uid,
                    },
                }
            );

            if (response.ok) {
                const pageData = await response.json();
                const formattedStrokes = pageData.strokes.map(toCanvasStroke);

                setPages(prevPages => {
                    const updatedPages = [...prevPages];
                    if (updatedPages[pageIndex]) {
                        updatedPages[pageIndex] = {
                            ...updatedPages[pageIndex],
                            strokes: formattedStrokes,
                            textElements: pageData.text_elements || [],
                            mediaElements: pageData.media_elements || [],
                            width: pageData.width || updatedPages[pageIndex].width,
                            height: pageData.height || updatedPages[pageIndex].height,
                            content_width: pageData.content_width || pageData.width || updatedPages[pageIndex].content_width,
                            content_height: pageData.content_height || pageData.height || updatedPages[pageIndex].content_height
                        };
                    }
                    return updatedPages;
                });
                loadedPagesRef.current.add(pageId);
            }
        } catch (error) {
            console.error('Error loading page content:', error);
        } finally {
            setPageStrokesLoading(false);
        }
    }, [notebook?.id]);

    // Load content for the current page when navigating
    useEffect(() => {
        if (pages.length > 0 && currentPageIndex >= 0 && currentPageIndex < pages.length) {
            const currentPage = pages[currentPageIndex];
            if (currentPage && !loadedPagesRef.current.has(currentPage.id)) {
                setPageStrokesLoading(true);
                loadPageContent(currentPage.id, currentPageIndex);
            } else {
                setPageStrokesLoading(false);
            }
        } else {
            setPageStrokesLoading(false);
        }
    }, [currentPageIndex, pages.length, loadPageContent]);

    // Recalculate canvas size when panels visibility changes
    useEffect(() => {
        const calculateCanvasSize = () => {
            const currentPage = pages[currentPageIndex];
            
            if (notebook?.page_mode === 'infinite' && currentPage) {
                const newCanvasSize = {
                    width: currentPage.width || 1200,
                    height: currentPage.height || 800
                };
                
                setCanvasSize(prevSize => {
                    if (prevSize.width !== newCanvasSize.width || prevSize.height !== newCanvasSize.height) {
                        return newCanvasSize;
                    }
                    return prevSize;
                });
                return;
            }
            
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            
            const newCanvasSize = { width: viewportWidth, height: viewportHeight };
            
            setCanvasSize(prevSize => {
                if (prevSize.width !== newCanvasSize.width || prevSize.height !== newCanvasSize.height) {
                    return newCanvasSize;
                }
                return prevSize;
            });
        };

        const timer = setTimeout(calculateCanvasSize, 100);
        return () => clearTimeout(timer);
    }, [pages.length, currentPageIndex, notebook?.page_mode]);

    const loadNotebookPages = async () => {
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                console.error("User is not authenticated.");
                return;
            }

            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${notebook.id}/`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                        "requesting-user-uid": auth.currentUser.uid,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            setPages(prevPages => {
                const newFormattedPages = data.pages.map(page => {
                    const existingPage = prevPages.find(p => p.id === page.id);
                    if (existingPage) {
                        return {
                            ...existingPage,
                            pageNumber: page.page_number,
                            title: page.title,
                            width: page.width || existingPage.width || 1200,
                            height: page.height || existingPage.height || 800,
                            content_width: page.content_width || existingPage.content_width || page.width || existingPage.width || 1200,
                            content_height: page.content_height || existingPage.content_height || page.height || existingPage.height || 800,
                            updatedAt: page.updated_at
                        };
                    } else {
                        return {
                            id: page.id,
                            notebookId: notebook.id,
                            pageNumber: page.page_number,
                            title: page.title,
                            width: page.width || 1200,
                            height: page.height || 800,
                            content_width: page.content_width || page.width || 1200,
                            content_height: page.content_height || page.height || 800,
                            strokes: [],
                            textElements: [],
                            createdAt: page.created_at,
                            updatedAt: page.updated_at
                        };
                    }
                });
                
                return newFormattedPages;
            });
        } catch (error) {
            console.error('Error loading notebook pages:', error);
        }
    };

    // Helper function to calculate bounds of a single stroke
    const calculateStrokeBounds = (stroke) => {
        if (!stroke.points || stroke.points.length === 0) {
            return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
        }
        
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        
        stroke.points.forEach(point => {
            minX = Math.min(minX, point.x);
            minY = Math.min(minY, point.y);
            maxX = Math.max(maxX, point.x);
            maxY = Math.max(maxY, point.y);
        });
        
        return {
            minX: isFinite(minX) ? minX : 0,
            minY: isFinite(minY) ? minY : 0,
            maxX: isFinite(maxX) ? maxX : 0,
            maxY: isFinite(maxY) ? maxY : 0
        };
    };

    const handleStrokeAdded = async (strokeOrEvent) => {
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) return;

            // Handle eraser action
            if (strokeOrEvent.action === 'erase') {
                saveStateToUndo();
                
                const strokeToErase = strokeOrEvent.stroke;
                const currentPage = pages[currentPageIndexRef.current];
                const currentPageId = currentPage?.id;
                
                if (!currentPageId) {
                    console.error('No current page ID found for eraser action');
                    return;
                }
                
                // Optimistic update: Remove stroke from local state immediately
                setPages(prevPages => {
                    const currentIndex = currentPageIndexRef.current;
                    const updatedPages = [...prevPages];
                    const updatedPage = { ...updatedPages[currentIndex] };
                    updatedPage.strokes = updatedPage.strokes.filter(s => s.id !== strokeToErase.id);
                    updatedPage.updatedAt = new Date().toISOString();
                    updatedPages[currentIndex] = updatedPage;
                    return updatedPages;
                });
                
                // Delete from backend
                if (!String(strokeToErase.id).startsWith('temp_')) {
                    try {
                        await fetch(
                            `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${notebook.id}/pages/${currentPageId}/strokes/${strokeToErase.id}/delete/`,
                            {
                                method: "DELETE",
                                headers: {
                                    "Content-Type": "application/json",
                                    "auth-token": `Bearer ${token}`,
                                    "Authorization": `${token}`,
                                    "requesting-user-uid": auth.currentUser.uid,
                                },
                            }
                        );
                    } catch (deleteError) {
                        console.error('Error deleting stroke from backend:', deleteError);
                    }
                }
                return;
            }
            
            // Handle area-based erasing with partial stroke removal
            if (strokeOrEvent.action === 'area-erase') {
                saveStateToUndo();
                
                const { strokesToRemove, strokesToAdd } = strokeOrEvent;
                const currentPage = pages[currentPageIndexRef.current];
                const currentPageId = currentPage?.id;
                
                if (!currentPageId) {
                    console.error('No current page ID found for area-erase action');
                    return;
                }
                
                // Optimistic update
                setPages(prevPages => {
                    const currentIndex = currentPageIndexRef.current;
                    const updatedPages = [...prevPages];
                    const updatedPage = { ...updatedPages[currentIndex] };
                    
                    const strokeIdsToRemove = strokesToRemove.map(s => s.id);
                    updatedPage.strokes = updatedPage.strokes.filter(s => !strokeIdsToRemove.includes(s.id));
                    updatedPage.strokes = [...updatedPage.strokes, ...strokesToAdd];
                    updatedPage.updatedAt = new Date().toISOString();
                    
                    updatedPages[currentIndex] = updatedPage;
                    return updatedPages;
                });
                
                // Delete old strokes from backend
                for (const strokeToRemove of strokesToRemove) {
                    if (!String(strokeToRemove.id).startsWith('temp_')) {
                        try {
                            await fetch(
                                `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${notebook.id}/pages/${currentPageId}/strokes/${strokeToRemove.id}/delete/`,
                                {
                                    method: "DELETE",
                                    headers: {
                                        "Content-Type": "application/json",
                                        "auth-token": `Bearer ${token}`,
                                        "Authorization": `${token}`,
                                        "requesting-user-uid": auth.currentUser.uid,
                                    },
                                }
                            );
                        } catch (deleteError) {
                            console.error('Error deleting stroke from backend during area erase:', deleteError);
                        }
                    }
                }
                
                // Add new stroke segments to backend
                for (const newStroke of strokesToAdd) {
                    try {
                        const response = await fetch(
                            `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${notebook.id}/pages/${currentPageId}/strokes/`,
                            {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json",
                                    "auth-token": `Bearer ${token}`,
                                    "Authorization": `${token}`,
                                    "requesting-user-uid": auth.currentUser.uid,
                                },
                                body: JSON.stringify({
                                    stroke_data: {
                                        points: newStroke.points,
                                        tool: newStroke.tool,
                                        style: newStroke.style,
                                        rotation: newStroke.rotation || 0
                                    },
                                    tool_type: newStroke.tool,
                                    color: newStroke.style.color,
                                    width: newStroke.style.thickness,
                                    opacity: newStroke.style.opacity || 1.0,
                                    text_content: newStroke.text || null
                                }),
                            }
                        );

                        if (response.ok) {
                            const savedStroke = await response.json();
                            
                            setPages(prevPages => {
                                const currentIndex = currentPageIndexRef.current;
                                const finalPages = [...prevPages];
                                const finalPage = { ...finalPages[currentIndex] };
                                
                                finalPage.strokes = finalPage.strokes.map(s => 
                                    s.id === newStroke.id ? { ...s, id: savedStroke.id } : s
                                );
                                
                                finalPages[currentIndex] = finalPage;
                                return finalPages;
                            });
                        }
                    } catch (saveError) {
                        console.error('Error saving stroke segment to backend:', saveError);
                    }
                }
                
                return;
            }
            
            // Handle normal stroke addition
            const stroke = strokeOrEvent;
            saveStateToUndo();
            
            const currentPage = pages[currentPageIndexRef.current];
            const currentPageId = currentPage?.id;
            
            if (!currentPageId) {
                console.error('No current page ID found for stroke addition');
                return;
            }
            
            // Create temporary ID for optimistic update
            const tempId = `temp_${Date.now()}_${Math.random()}`;
            const optimisticStroke = {
                ...stroke,
                id: tempId,
                isPending: true
            };
            
            // Add stroke optimistically
            setPages(prevPages => {
                const currentIndex = currentPageIndexRef.current;
                const updatedPages = [...prevPages];
                const updatedPage = { ...updatedPages[currentIndex] };
                
                updatedPage.strokes = [...updatedPage.strokes, optimisticStroke];
                updatedPage.updatedAt = new Date().toISOString();
                
                updatedPages[currentIndex] = updatedPage;
                return updatedPages;
            });
            
            // Save stroke to backend
            try {
                const response = await fetch(
                    `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${notebook.id}/pages/${currentPageId}/strokes/`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "auth-token": `Bearer ${token}`,
                            "Authorization": `${token}`,
                            "requesting-user-uid": auth.currentUser.uid,
                        },
                        body: JSON.stringify({
                            stroke_data: {
                                points: stroke.points,
                                tool: stroke.tool,
                                style: stroke.style,
                                rotation: stroke.rotation || 0
                            },
                            tool_type: stroke.tool,
                            color: stroke.style.color,
                            width: stroke.style.thickness,
                            opacity: stroke.style.opacity || 1.0,
                            text_content: stroke.text || null
                        }),
                    }
                );

                if (response.ok) {
                    const savedStroke = await response.json();
                    
                    // Replace temporary stroke with saved stroke
                    setPages(prevPages => {
                        const currentIndex = currentPageIndexRef.current;
                        const finalPages = [...prevPages];
                        const finalPage = { ...finalPages[currentIndex] };
                        
                        finalPage.strokes = finalPage.strokes.map(s => 
                            s.id === tempId 
                                ? { ...stroke, id: savedStroke.id, isPending: false }
                                : s
                        );
                        finalPage.updatedAt = new Date().toISOString();
                        
                        finalPages[currentIndex] = finalPage;
                        return finalPages;
                    });
                } else {
                    // Mark stroke as failed
                    setPages(prevPages => {
                        const currentIndex = currentPageIndexRef.current;
                        const failedPages = [...prevPages];
                        const failedPage = { ...failedPages[currentIndex] };
                        
                        failedPage.strokes = failedPage.strokes.map(s => 
                            s.id === tempId ? { ...s, isPending: false, isFailed: true } : s
                        );
                        
                        failedPages[currentIndex] = failedPage;
                        return failedPages;
                    });
                    
                    console.error('Failed to save stroke to backend');
                }
            } catch (saveError) {
                // Mark stroke as failed
                setPages(prevPages => {
                    const currentIndex = currentPageIndexRef.current;
                    const failedPages = [...prevPages];
                    const failedPage = { ...failedPages[currentIndex] };
                    
                    failedPage.strokes = failedPage.strokes.map(s => 
                        s.id === tempId ? { ...s, isPending: false, isFailed: true } : s
                    );
                    
                    failedPages[currentIndex] = failedPage;
                    return failedPages;
                });
                
                console.error('Error saving stroke to backend:', saveError);
            }
        } catch (error) {
            console.error('Error in handleStrokeAdded:', error);
        }
    };

    const handleStrokesUpdated = async (updatedStrokes) => {
        try {
            const currentPage = pages[currentPageIndexRef.current];
            const currentPageId = currentPage?.id;

            if (!currentPageId) {
                console.error('No current page ID found for strokes update');
                return;
            }

            // Update local state immediately
            setPages(prevPages => {
                const currentIndex = currentPageIndexRef.current;
                const updatedPages = [...prevPages];
                const updatedPage = { ...updatedPages[currentIndex] };
                
                const updatedStrokesMap = new Map(updatedStrokes.map(stroke => [stroke.id, stroke]));
                
                updatedPage.strokes = updatedPage.strokes.map(existingStroke => 
                    updatedStrokesMap.has(existingStroke.id) 
                        ? updatedStrokesMap.get(existingStroke.id)
                        : existingStroke
                );
                
                updatedPage.updatedAt = new Date().toISOString();
                updatedPages[currentIndex] = updatedPage;
                return updatedPages;
            });

            // Sync to backend
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (token) {
                for (const stroke of updatedStrokes) {
                    if (!String(stroke.id).startsWith('temp_')) {
                        try {
                            await fetch(
                                `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${notebook.id}/pages/${currentPageId}/strokes/${stroke.id}/`,
                                {
                                    method: "PUT",
                                    headers: {
                                        "Content-Type": "application/json",
                                        "auth-token": `Bearer ${token}`,
                                        "Authorization": `${token}`,
                                        "requesting-user-uid": auth.currentUser.uid,
                                    },
                                    body: JSON.stringify({
                                        stroke_data: {
                                            points: stroke.points,
                                            tool: stroke.tool,
                                            style: stroke.style,
                                            rotation: stroke.rotation || 0
                                        },
                                        tool_type: stroke.tool,
                                        color: stroke.style.color,
                                        width: stroke.style.thickness,
                                        text_content: stroke.text || null
                                    }),
                                }
                            );
                        } catch (strokeError) {
                            console.error(`Error syncing stroke ${stroke.id}:`, strokeError);
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error in handleStrokesUpdated:', error);
        }
    };

    const handleToolChange = (tool) => {
        if (tool === 'ruler') {
            setRulerVisible(!rulerVisible);
            return;
        }
        setCurrentTool(tool);
    };

    const handleToolSettingChange = (setting, value) => {
        setToolSettings(prev => ({ ...prev, [setting]: value }));
        saveToolSettingsToBackend({ ...toolSettings, [setting]: value });
    };

    const saveToolSettingsToBackend = async (settings) => {
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) return;

            await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${notebook.id}/tool-settings/`,
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                        "requesting-user-uid": auth.currentUser.uid,
                    },
                    body: JSON.stringify({ tool_settings: settings }),
                }
            );
        } catch (error) {
            console.error('Error saving tool settings:', error);
        }
    };

    const handleBackgroundChange = async (newBackground) => {
        setCanvasBackground(newBackground);
        
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) return;

            await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${notebook.id}/`,
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                        "requesting-user-uid": auth.currentUser.uid,
                    },
                    body: JSON.stringify({ background_type: newBackground }),
                }
            );
        } catch (error) {
            console.error('Error saving background preference:', error);
        }
    };

    const toggleSidebar = () => setSidebarVisible(!sidebarVisible);
    const toggleToolPanel = () => setToolPanelVisible(!toolPanelVisible);
    const toggleSavedStrokesPanel = () => setSavedStrokesPanelVisible(!savedStrokesPanelVisible);

    const handleStrokeContextMenu = (e, stroke) => {
        e.preventDefault();
        setSelectedStrokeForSaving(stroke);
        setContextMenuPosition({ x: e.clientX, y: e.clientY });
        setContextMenuVisible(true);
    };

    const handleSaveStrokeAsCustom = async (strokeData, name, description) => {
        try {
            const stroke = strokeData || selectedStrokeForSaving;
            if (!stroke) return false;

            const response = await fetch('/api/saved_strokes/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${await auth.currentUser?.getIdToken()}`
                },
                body: JSON.stringify({
                    name,
                    description,
                    stroke_data: stroke,
                    thumbnail_data: generateStrokeThumbnail(stroke)
                })
            });

            if (!response.ok) throw new Error('Failed to save stroke');

            setSelectedStrokeForSaving(null);
            setContextMenuVisible(false);
            return true;
        } catch (error) {
            console.error('Error saving stroke:', error);
            return false;
        }
    };

    const handlePlaceSavedStroke = async (savedStroke, position) => {
        try {
            const strokeData = savedStroke.stroke_data;
            const strokeBounds = calculateStrokeBounds(strokeData);
            const offsetX = position.x - strokeBounds.minX;
            const offsetY = position.y - strokeBounds.minY;

            const newStroke = {
                ...strokeData,
                id: 'stroke_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
                points: strokeData.points.map(point => ({
                    ...point,
                    x: point.x + offsetX,
                    y: point.y + offsetY
                })),
                timestamp: new Date().toISOString()
            };

            handleStrokeAdded(newStroke);

            fetch(`/api/saved_strokes/${savedStroke.id}/use/`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${await auth.currentUser?.getIdToken()}` }
            }).catch(error => console.error('Error incrementing usage:', error));
        } catch (error) {
            console.error('Error placing saved stroke:', error);
        }
    };

    const handlePlaceShape = async (shape, position) => {
        try {
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            
            shape.points.forEach(point => {
                minX = Math.min(minX, point.x);
                minY = Math.min(minY, point.y);
                maxX = Math.max(maxX, point.x);
                maxY = Math.max(maxY, point.y);
            });

            const shapeCenterX = (minX + maxX) / 2;
            const shapeCenterY = (minY + maxY) / 2;
            const offsetX = position.x - shapeCenterX;
            const offsetY = position.y - shapeCenterY;

            const newStroke = {
                ...shape,
                id: 'shape_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
                points: shape.points.map(point => ({
                    ...point,
                    x: point.x + offsetX,
                    y: point.y + offsetY
                })),
                timestamp: new Date().toISOString()
            };

            handleStrokeAdded(newStroke);
        } catch (error) {
            console.error('Error placing shape:', error);
        }
    };

    const generateStrokeThumbnail = (stroke) => {
        if (!stroke.points || stroke.points.length === 0) return '';

        const bounds = calculateStrokeBounds(stroke);
        const padding = 5;
        const thumbnailSize = 100;
        
        const scaleX = (thumbnailSize - 2 * padding) / (bounds.maxX - bounds.minX || 1);
        const scaleY = (thumbnailSize - 2 * padding) / (bounds.maxY - bounds.minY || 1);
        const scale = Math.min(scaleX, scaleY);

        const scaledPoints = stroke.points.map(point => ({
            x: (point.x - bounds.minX) * scale + padding,
            y: (point.y - bounds.minY) * scale + padding
        }));

        const pathData = scaledPoints.map((point, index) => 
            index === 0 ? `M ${point.x} ${point.y}` : `L ${point.x} ${point.y}`
        ).join(' ');

        return `<svg width="${thumbnailSize}" height="${thumbnailSize}" xmlns="http://www.w3.org/2000/svg">
            <path d="${pathData}" stroke="${stroke.style?.color || '#000'}" stroke-width="${(stroke.style?.strokeWidth || 2) * scale}" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;
    };

    const toggleHeader = () => setHeaderMinimized(!headerMinimized);

    const addNewPage = async () => {
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) return;

            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${notebook.id}/pages/create/`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                        "requesting-user-uid": auth.currentUser.uid,
                    },
                    body: JSON.stringify({ title: `Page ${pages.length + 1}` }),
                }
            );

            if (response.ok) {
                const newPageData = await response.json();
                const newPage = {
                    id: newPageData.id,
                    notebookId: notebook.id,
                    pageNumber: newPageData.page_number,
                    title: newPageData.title,
                    width: newPageData.width || 1200,
                    height: newPageData.height || 800,
                    content_width: newPageData.content_width || newPageData.width || 1200,
                    content_height: newPageData.content_height || newPageData.height || 800,
                    strokes: [],
                    textElements: [],
                    createdAt: newPageData.created_at,
                    updatedAt: newPageData.updated_at
                };
                
                const updatedPages = [...pages, newPage];
                setPages(updatedPages);
                setCurrentPageIndex(updatedPages.length - 1);
            }
        } catch (error) {
            console.error('Error creating new page:', error);
            alert('Failed to create new page. Please try again.');
        }
    };

    const deleteCurrentPage = async () => {
        if (pages.length <= 1) {
            alert('Cannot delete the last page. A notebook must have at least one page.');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this page? This action cannot be undone.')) {
            return;
        }

        try {
            const currentPage = pages[currentPageIndex];
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) return;
            
            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${notebook.id}/pages/${currentPage.id}/`,
                {
                    method: "DELETE",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                        "requesting-user-uid": auth.currentUser.uid,
                    },
                }
            );

            if (response.ok) {
                const updatedPages = pages.filter((_, index) => index !== currentPageIndex);
                const reorderedPages = updatedPages.map((page, index) => ({
                    ...page,
                    pageNumber: index + 1
                }));
                setPages(reorderedPages);
                
                const newIndex = currentPageIndex >= updatedPages.length 
                    ? updatedPages.length - 1 
                    : currentPageIndex;
                setCurrentPageIndex(newIndex);
                
                loadedPagesRef.current.delete(currentPage.id);
            } else {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
        } catch (error) {
            alert('Failed to delete page. Please try again.');
        }
    };

    const navigateToPage = async (pageIndex) => {
        if (pageIndex >= 0 && pageIndex < pages.length) {
            setCurrentPageIndex(pageIndex);
            setUndoStack([]);
            setRedoStack([]);
        }
    };

    const clearCurrentPage = async () => {
        if (!window.confirm('Are you sure you want to clear this page? This can be undone with Ctrl+Z.')) {
            return;
        }

        saveStateToUndo();
        
        try {
            const currentPage = pages[currentPageIndex];
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) return;
            
            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${notebook.id}/pages/${currentPage.id}/clear/`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                        "requesting-user-uid": auth.currentUser.uid,
                    },
                }
            );

            if (response.ok) {
                const updatedPages = [...pages];
                const updatedPage = { ...updatedPages[currentPageIndex] };
                
                updatedPage.strokes = [];
                updatedPage.textElements = [];
                updatedPage.updatedAt = new Date().toISOString();
                
                updatedPages[currentPageIndex] = updatedPage;
                setPages(updatedPages);
            }
        } catch (error) {
            console.error('Error clearing page:', error);
            alert('Failed to clear page. Please try again.');
        }
    };

    // Title editing functions
    const handleTitleEdit = () => {
        setIsEditingTitle(true);
        setEditingTitleValue(notebook.title);
    };

    const handleTitleSave = async () => {
        if (!editingTitleValue.trim()) {
            alert('Notebook title cannot be empty.');
            return;
        }

        if (editingTitleValue.trim() === notebook.title) {
            setIsEditingTitle(false);
            return;
        }

        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) return;

            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${notebook.id}/`,
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                        "requesting-user-uid": user.uid,
                    },
                    body: JSON.stringify({ title: editingTitleValue.trim() }),
                }
            );

            if (response.ok) {
                const updatedNotebookData = await response.json();
                notebook.title = updatedNotebookData.title;
                notebook.updated_at = updatedNotebookData.updated_at;
                setIsEditingTitle(false);
            } else {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
        } catch (error) {
            console.error('Error renaming notebook:', error);
            alert('Failed to rename notebook. Please try again.');
        }
    };

    const handleTitleCancel = () => {
        setIsEditingTitle(false);
        setEditingTitleValue(notebook.title);
    };

    const handleTitleKeyPress = (e) => {
        if (e.key === 'Enter') handleTitleSave();
        else if (e.key === 'Escape') handleTitleCancel();
    };

    // Undo/Redo functionality
    const saveStateToUndo = () => {
        const currentPageState = {
            pageIndex: currentPageIndex,
            strokes: [...pages[currentPageIndex].strokes],
            timestamp: Date.now()
        };
        
        setUndoStack(prevStack => {
            const newStack = [...prevStack, currentPageState];
            if (newStack.length > maxUndoSteps) {
                return newStack.slice(-maxUndoSteps);
            }
            return newStack;
        });
        
        setRedoStack([]);
    };

    const performUndo = async () => {
        if (undoStack.length === 0) return;
        
        const currentPageState = {
            pageIndex: currentPageIndex,
            strokes: [...pages[currentPageIndex].strokes],
            timestamp: Date.now()
        };
        
        setRedoStack(prevStack => [...prevStack, currentPageState]);
        
        const lastState = undoStack[undoStack.length - 1];
        setUndoStack(prevStack => prevStack.slice(0, -1));
        
        setPages(prevPages => {
            const updatedPages = [...prevPages];
            const updatedPage = { ...updatedPages[lastState.pageIndex] };
            updatedPage.strokes = [...lastState.strokes];
            updatedPage.updatedAt = new Date().toISOString();
            updatedPages[lastState.pageIndex] = updatedPage;
            return updatedPages;
        });
    };

    const performRedo = async () => {
        if (redoStack.length === 0) return;
        
        const currentPageState = {
            pageIndex: currentPageIndex,
            strokes: [...pages[currentPageIndex].strokes],
            timestamp: Date.now()
        };
        
        setUndoStack(prevStack => [...prevStack, currentPageState]);
        
        const redoState = redoStack[redoStack.length - 1];
        setRedoStack(prevStack => prevStack.slice(0, -1));
        
        setPages(prevPages => {
            const updatedPages = [...prevPages];
            const updatedPage = { ...updatedPages[redoState.pageIndex] };
            updatedPage.strokes = [...redoState.strokes];
            updatedPage.updatedAt = new Date().toISOString();
            updatedPages[redoState.pageIndex] = updatedPage;
            return updatedPages;
        });
    };

    const canUndo = () => undoStack.length > 0;
    const canRedo = () => redoStack.length > 0;

    // Dimension control functions
    const openDimensionModal = () => {
        const currentPage = pages[currentPageIndex];
        if (currentPage) {
            setDimensionInputs({ 
                width: currentPage.width || canvasSize.width || 1200, 
                height: currentPage.height || canvasSize.height || 800 
            });
        }
        setShowDimensionModal(true);
    };

    const closeDimensionModal = () => setShowDimensionModal(false);

    const handleDimensionChange = async () => {
        try {
            if (dimensionInputs.width < 100 || dimensionInputs.width > 10000 || 
                dimensionInputs.height < 100 || dimensionInputs.height > 10000) {
                alert('Dimensions must be between 100px and 10,000px');
                return;
            }

            const currentPage = pages[currentPageIndex];
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            const newSize = { width: dimensionInputs.width, height: dimensionInputs.height };

            setCanvasSize(newSize);

            setPages(prevPages => {
                const updatedPages = [...prevPages];
                updatedPages[currentPageIndex] = {
                    ...updatedPages[currentPageIndex],
                    width: newSize.width,
                    height: newSize.height,
                    content_width: newSize.width,
                    content_height: newSize.height,
                    updatedAt: new Date().toISOString()
                };
                return updatedPages;
            });

            if (token && currentPage?.id) {
                await fetch(
                    `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${notebook.id}/pages/${currentPage.id}/resize/`,
                    {
                        method: "PATCH",
                        headers: {
                            "Content-Type": "application/json",
                            "auth-token": `Bearer ${token}`,
                            "Authorization": `${token}`,
                            "requesting-user-uid": auth.currentUser.uid,
                        },
                        body: JSON.stringify({
                            width: newSize.width,
                            height: newSize.height,
                            content_width: newSize.width,
                            content_height: newSize.height
                        })
                    }
                );
            }

            setShowDimensionModal(false);
        } catch (error) {
            console.error('Error updating dimensions:', error);
            alert('Failed to update dimensions. Please try again.');
        }
    };

    // Keyboard shortcuts for undo/redo
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (isEditingTitle) return;
            
            if ((event.ctrlKey || event.metaKey) && !event.shiftKey && event.key === 'z') {
                event.preventDefault();
                if (canUndo()) performUndo();
            } else if ((event.ctrlKey || event.metaKey) && (event.key === 'y' || (event.shiftKey && event.key === 'Z'))) {
                event.preventDefault();
                if (canRedo()) performRedo();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isEditingTitle]);

    // Set initial panel visibility based on screen size
    useEffect(() => {
        const checkScreenSize = () => {
            const isMobile = window.innerWidth <= 768;
            if (isMobile) {
                setSidebarVisible(false);
                setToolPanelVisible(false);
            }
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    const currentPage = pages[currentPageIndex];

    // Show loading state
    if (!notebook || loading || pageStrokesLoading || pages.length === 0) {
        return (
            <div className="loading-animation-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
                <div className="coffee-cup-loader">
                    <div className="cup">
                        <div className="cup-body"></div>
                        <div className="cup-handle"></div>
                        <div className="cup-base"></div>
                        <div className="cup-shadow"></div>
                        <div className="steam steam1"></div>
                        <div className="steam steam2"></div>
                        <div className="steam steam3"></div>
                    </div>
                </div>
                <p style={{ color: 'var(--bb-accent)', fontSize: '1.1rem', marginTop: 16 }}>
                    {loadingMessage || 'Loading notebook...'}
                </p>
            </div>
        );
    }

    if (!currentPage) {
        return (
            <div className="loading-animation-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
                <div className="coffee-cup-loader">
                    <div className="cup">
                        <div className="cup-body"></div>
                        <div className="cup-handle"></div>
                        <div className="cup-base"></div>
                        <div className="cup-shadow"></div>
                        <div className="steam steam1"></div>
                        <div className="steam steam2"></div>
                        <div className="steam steam3"></div>
                    </div>
                </div>
                <p style={{ color: 'var(--bb-accent)', fontSize: '1.1rem', marginTop: 16 }}>Preparing page...</p>
            </div>
        );
    }

    return (
        <div className={`notebook-editor-fullscreen 
            ${!toolPanelVisible ? 'tools-hidden' : ''} 
            ${!sidebarVisible ? 'pages-hidden' : ''}`}
            data-page-mode={notebook?.page_mode || 'scaling'}>
            
            {/* Full-screen Canvas Background */}
            <div className="fullscreen-canvas-area">
                <DrawingCanvas
                    key={`page-${currentPage.id}`}
                    width={canvasSize.width}
                    height={canvasSize.height}
                    tool={currentTool}
                    toolSettings={toolSettings}
                    strokes={currentPage?.strokes || []}
                    onStrokeAdded={handleStrokeAdded}
                    onStrokesUpdated={handleStrokesUpdated}
                    onStrokeContextMenu={handleStrokeContextMenu}
                    background={canvasBackground}
                    palmRejectionSettings={palmRejectionSettings}
                    rulerVisible={rulerVisible}
                    setRulerVisible={setRulerVisible}
                    pageMode={notebook?.page_mode || 'scaling'}
                    contentBounds={notebook?.page_mode === 'infinite' ? {
                        width: currentPage.content_width || currentPage.width,
                        height: currentPage.content_height || currentPage.height
                    } : null}
                />
            </div>

            {/* Floating Header */}
            <NotebookHeader
                headerMinimized={headerMinimized}
                onBack={onBack}
                isEditingTitle={isEditingTitle}
                editingTitleValue={editingTitleValue}
                setEditingTitleValue={setEditingTitleValue}
                handleTitleKeyPress={handleTitleKeyPress}
                handleTitleSave={handleTitleSave}
                handleTitleCancel={handleTitleCancel}
                handleTitleEdit={handleTitleEdit}
                notebook={notebook}
                currentPageIndex={currentPageIndex}
                pages={pages}
                toolPanelVisible={toolPanelVisible}
                toggleToolPanel={toggleToolPanel}
                sidebarVisible={sidebarVisible}
                toggleSidebar={toggleSidebar}
                performUndo={performUndo}
                performRedo={performRedo}
                canUndo={canUndo}
                canRedo={canRedo}
                clearCurrentPage={clearCurrentPage}
                deleteCurrentPage={deleteCurrentPage}
                addNewPage={addNewPage}
                toggleHeader={toggleHeader}
                openDimensionModal={openDimensionModal}
                canvasSize={canvasSize}
                pageMode={notebook?.page_mode}
                canvasBackground={canvasBackground}
                loadedPageIds={loadedPagesRef.current}
            />

            {/* Floating Tool Panel */}
            <ToolPanel
                toolPanelVisible={toolPanelVisible}
                toggleToolPanel={toggleToolPanel}
                currentTool={currentTool}
                handleToolChange={handleToolChange}
                toolSettings={toolSettings}
                handleToolSettingChange={handleToolSettingChange}
                canvasBackground={canvasBackground}
                handleBackgroundChange={handleBackgroundChange}
                isSubscriber={isSubscriber}
                rulerVisible={rulerVisible}
                onToggleSavedStrokes={toggleSavedStrokesPanel}
                onPlaceShape={handlePlaceShape}
            />

            {/* Saved Strokes Panel */}
            <SavedStrokesPanel
                visible={savedStrokesPanelVisible}
                onClose={() => {
                    setSavedStrokesPanelVisible(false);
                    setSelectedStrokeForSaving(null);
                }}
                onPlaceStroke={handlePlaceSavedStroke}
                onSaveStroke={handleSaveStrokeAsCustom}
                strokeToSave={selectedStrokeForSaving}
            />

            {/* Floating Page Navigation */}
            <PageNavigation
                sidebarVisible={sidebarVisible}
                currentPageIndex={currentPageIndex}
                pages={pages}
                navigateToPage={navigateToPage}
            />

            <EditTitleInNotebook
                isEditingTitle={isEditingTitle}
                editingTitleValue={editingTitleValue}
                setEditingTitleValue={setEditingTitleValue}
                handleTitleKeyPress={handleTitleKeyPress}
                handleTitleSave={handleTitleSave}
                handleTitleCancel={handleTitleCancel}
                handleTitleEdit={handleTitleEdit}
                notebook={notebook}
            />

            {/* Dimension Control Modal */}
            {showDimensionModal && (
                <div className="modal-overlay" onClick={closeDimensionModal}>
                    <div className="dimension-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="NotebookEditor__dimension-modal-header">
                            <h3>Change Canvas Dimensions</h3>
                        </div>
                        
                        <div className="modal-content">
                            <p className="current-dimensions">
                                Current: {canvasSize.width} × {canvasSize.height} pixels
                            </p>
                            
                            <div className="dimensions-input-row">
                                <div className="dimension-input">
                                    <label>Width (px)</label>
                                    <input
                                        type="number"
                                        min="100"
                                        max="10000"
                                        value={dimensionInputs.width}
                                        onChange={(e) => setDimensionInputs(prev => ({
                                            ...prev,
                                            width: parseInt(e.target.value) || 1200
                                        }))}
                                    />
                                </div>
                                <div className="dimension-input">
                                    <label>Height (px)</label>
                                    <input
                                        type="number"
                                        min="100"
                                        max="10000"
                                        value={dimensionInputs.height}
                                        onChange={(e) => setDimensionInputs(prev => ({
                                            ...prev,
                                            height: parseInt(e.target.value) || 800
                                        }))}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        <div className="modal-actions">
                            <button className="cancel-button" onClick={closeDimensionModal}>
                                Cancel
                            </button>
                            <button className="apply-button" onClick={handleDimensionChange}>
                                Apply Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Context Menu for Stroke Actions */}
            {contextMenuVisible && selectedStrokeForSaving && (
                <div 
                    style={{
                        position: 'fixed',
                        top: contextMenuPosition.y,
                        left: contextMenuPosition.x,
                        background: 'var(--bb-shell-bg)',
                        border: '1px solid var(--bb-accent)',
                        borderRadius: '8px',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                        zIndex: 3000,
                        minWidth: '200px',
                        maxWidth: '280px'
                    }}
                    onMouseLeave={() => setContextMenuVisible(false)}
                >
                    <div
                        style={{
                            padding: '12px 16px',
                            color: 'var(--bb-shell-text)',
                            cursor: 'pointer',
                            fontSize: '15px',
                            borderRadius: '8px',
                            transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.background = 'rgba(91, 146, 229, 0.1)'}
                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        onClick={() => {
                            setSavedStrokesPanelVisible(true);
                            setContextMenuVisible(false);
                        }}
                    >
                        <i className="fas fa-save" style={{ marginRight: '8px' }}></i>
                        Save as Custom Stroke
                    </div>
                    
                    <div style={{
                        padding: '8px 16px',
                        fontSize: '12px',
                        color: 'rgba(255,255,255,0.6)',
                        borderTop: '1px solid rgba(91, 146, 229, 0.2)',
                        background: 'rgba(0,0,0,0.1)'
                    }}>
                        💡 Use Select tool + right-click on strokes
                    </div>
                </div>
            )}
        </div>
    );
}

export default NotebookEditor;
